/* @flow */

import { compose } from 'ramda';
import * as React from 'react';
import { Helmet } from 'react-helmet';

import useComponentPreload from './hook/useComponentPreload';
import useInitialPayloadFetching from './hook/useInitialPayloadFetching';
import useJSClass from './hook/useJSClass';
import useServerStylesRemoval from './hook/useServerStylesRemoval';
import useTimeFormatPatch from './hook/useTimeFormatPatch';
import useTimezone from './hook/useTimezone';
import useTouchDetection from './hook/useTouchDetection';
import useUserReportingRegistration from './hook/useUserReportingRegistration';
import useValidationMessages from './hook/useValidationMessages';
import HTTPStatusCodeSwitch from './switch/HTTPStatusCodeSwitch';
import RedirectSwitch from './switch/RedirectSwitch';
import TemplateSwitch from './switch/TemplateSwitch';
import HelpSwitch from './switch/HelpSwitch';
import useMobileKeyboardVisibility from 'src/shared/app/base/hook/useMobileKeyboardVisibility';
import useMounted from 'src/shared/app/base/hook/useMounted';
import ModalCenter from 'src/shared/app/base/modal/component/ModalCenter';
import useLiveChatVisitorAuth from 'plugin/live-chat/hook/useLiveChatVisitorAuth';
import NotificationPortal from 'src/shared/app/base/portal/notification/NotificationPortal';
import useRouteRegistration from 'src/shared/app/base/route/hook/useRouteRegistration';
import usePageTracking from 'src/shared/app/base/tracking/hook/usePageTracking';
import useTrackAbandons from 'src/shared/app/base/tracking/hook/useTrackAbandons';
import useTTITracking from 'src/shared/app/base/tracking/hook/useTTITracking';
import useFeatureDecisions from 'src/shared/app/feature/hook/useFeatureDecisions';
import useFeatureRouter from 'src/shared/app/feature/hook/useFeatureRouter';
import useGlobalNotifications from 'src/shared/app/notification/hook/useGlobalNotifications';
import useEventBraindateName from 'src/shared/domain/event/hook/useEventBraindateName';
import useOauthMessages from 'src/shared/app/account/oauth/hook/useOauthMessages';
import LiveChatButton from 'plugin/live-chat/component/LiveChatButton';
import TabNav from 'src/shared/ui/component/button/TabNav';
import useIsLogged from 'src/shared/app/authentication/hook/useIsLogged';
import useLiveChat from 'plugin/live-chat/hook/useLiveChat';

const App = (): React.Node => {
  /*
   |----------------------------------------------------------------------------
   | Hooks
   |----------------------------------------------------------------------------
   */

  // Initialize feature router
  useFeatureRouter();

  // Routes
  useRouteRegistration();

  // Calls to API
  useInitialPayloadFetching();
  useTimeFormatPatch();

  // Websockets
  useGlobalNotifications();

  // OauthMessages
  useOauthMessages();

  // DOM manipulation
  useJSClass();
  useTouchDetection();
  useServerStylesRemoval();

  // Preload
  useComponentPreload();

  // Tracking
  usePageTracking();
  useTrackAbandons();
  useTTITracking();

  // Libraries/Services configuration
  useUserReportingRegistration();
  useValidationMessages();
  useTimezone();

  // Plugins
  useLiveChatVisitorAuth();

  const { braindatePlatform } = useEventBraindateName();
  const { isHelpEnabled } = useFeatureDecisions();
  const isMounted = useMounted();
  const isMobileKeyboardVisible = useMobileKeyboardVisibility();
  const isAuth = useIsLogged();
  const hasLiveChat = useLiveChat() && isAuth && isMounted;

  /*
   |----------------------------------------------------------------------------
   | Elements
   |----------------------------------------------------------------------------
   */

  const helpSwitch = isHelpEnabled && isMounted && !isMobileKeyboardVisible && (
    <HelpSwitch />
  );
  const liveChat = hasLiveChat && <LiveChatButton />;
  const modalCenter = isMounted && <ModalCenter />;
  const notificationPortal = isMounted && <NotificationPortal />;

  return (
    <React.Fragment>
      <RedirectSwitch />
      <HTTPStatusCodeSwitch />
      <TemplateSwitch />

      <TabNav
        items={[
          { key: 1, children: liveChat },
          { key: 2, children: helpSwitch },
        ]}
      />

      <Helmet
        defaultTitle={braindatePlatform}
        titleTemplate={`%s - ${braindatePlatform}`}
      ></Helmet>

      {modalCenter}
      {notificationPortal}
    </React.Fragment>
  );
};

export default compose(React.memo)(App);
